/**
 * API base URL for the backend (optimus.Ink.backend).
 * Set VITE_API_URL in .env when the backend runs on a different host/port.
 */


export const getApiBaseUrl = () => {
    if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;
    if (import.meta.env.VITE_BRAND === 'mykiosk') return 'https://api.mykiosk.ai';
    if (import.meta.env.VITE_BRAND === 'kevo') return 'https://api.kevo.store';
    if (import.meta.env.VITE_BRAND === 'kevo_sandbox') return 'https://devapi.kevo.store';
    return 'https://api.askmybio.ai'; // default (optimus)
};

/**
 * Django backend that hosts AI Voice — voice cloning, LiveKit token
 * minting, etc. Same brand-switch shape as getApiBaseUrl above.
 */
export const getDjangoApiBaseUrl = () => {
    if (import.meta.env.VITE_BRAND === 'mykiosk') return 'https://devapi-v1.kevo.store';
    if (import.meta.env.VITE_BRAND === 'kevo') return 'https://api-v1.kevo.store';
    if (import.meta.env.VITE_BRAND === 'kevo_sandbox') return 'https://devapi-v1.kevo.store';
    return 'https://devapi-v1.kevo.store'; // default (optimus)
};

export const getDjangoApiKey = () => '11'

/**
 * Origin of the Next.js SEO site that hosts /sign-in, /sign-up and the public
 * marketing pages. In dev the SEO site runs on :3009; in prod it typically
 * shares the domain with the app (reverse proxy routes /app/* to this SPA and
 * /sign-in etc. to SEO). Override with VITE_SEO_URL if needed.
 */
export const getSeoBaseUrl = () => {
    const explicit = import.meta.env.VITE_SEO_URL;
    if (explicit) return String(explicit).replace(/\/$/, '');
    if (typeof window === 'undefined') return '';
    const { hostname, protocol, host } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
    if (isLocalhost) return `${protocol}//${hostname}:3009`;
    return `${protocol}//${host}`;
};

/**
 * Public YouTube channel lookup by handle. Returns channel snippet + stats
 * (title, avatar, subscribers, videos) or null if not found.
 *
 * Caches results in localStorage for 6h (positive) / 10m (negative) so the
 * editor doesn't re-hit the backend on every blur/reload. The backend has its
 * own in-memory cache too — together they keep YouTube Data API quota safe.
 */
const YT_LS_KEY = 'ytChannelLookupCache_v1';
const YT_LS_TTL_OK_MS = 6 * 60 * 60 * 1000;
const YT_LS_TTL_NEG_MS = 10 * 60 * 1000;

function ytLsRead() {
    try { return JSON.parse(localStorage.getItem(YT_LS_KEY) || '{}') || {}; } catch { return {}; }
}
function ytLsWrite(obj) {
    try { localStorage.setItem(YT_LS_KEY, JSON.stringify(obj)); } catch { /* quota / private mode */ }
}

export async function lookupYoutubeChannel(handle, { force = false } = {}) {
    const h = String(handle || '').trim().replace(/^@/, '').toLowerCase();
    if (!h) return null;

    if (!force) {
        const cache = ytLsRead();
        const hit = cache[h];
        if (hit && hit.expiresAt > Date.now()) {
            return hit.channel || null;
        }
    }

    try {
        const res = await fetch(`${getApiBaseUrl()}/api/social/youtube/lookup?handle=${encodeURIComponent(h)}${force ? '&refresh=1' : ''}`);
        if (!res.ok) return null;
        const json = await res.json();
        const ch = json?.data?.channel || json?.channel || null;

        const cache = ytLsRead();
        cache[h] = { channel: ch, expiresAt: Date.now() + (ch ? YT_LS_TTL_OK_MS : YT_LS_TTL_NEG_MS) };
        // Drop expired entries to prevent unbounded growth
        const now = Date.now();
        for (const k of Object.keys(cache)) {
            if (!cache[k] || cache[k].expiresAt < now) delete cache[k];
        }
        ytLsWrite(cache);

        return ch;
    } catch {
        return null;
    }
}

/**
 * Public Instagram profile lookup by handle. Returns profile info
 * (username, avatar, bio, followers, etc.) or null if not found.
 *
 * Caches results in localStorage for 6h (positive) / 10m (negative) so the
 * editor doesn't re-hit the backend on every blur/reload.
 */
const IG_LS_KEY = 'igProfileLookupCache_v1';
const IG_LS_TTL_OK_MS = 6 * 60 * 60 * 1000;
const IG_LS_TTL_NEG_MS = 10 * 60 * 1000;

function igLsRead() {
    try { return JSON.parse(localStorage.getItem(IG_LS_KEY) || '{}') || {}; } catch { return {}; }
}
function igLsWrite(obj) {
    try { localStorage.setItem(IG_LS_KEY, JSON.stringify(obj)); } catch { /* quota / private mode */ }
}

export async function lookupInstagramProfile(handle, { force = false } = {}) {
    const h = String(handle || '').trim().replace(/^@/, '').toLowerCase();
    if (!h) return null;

    if (!force) {
        const cache = igLsRead();
        const hit = cache[h];
        if (hit && hit.expiresAt > Date.now()) {
            return hit.profile || null;
        }
    }

    try {
        const res = await fetch(`${getApiBaseUrl()}/api/social/instagram/lookup?handle=${encodeURIComponent(h)}${force ? '&refresh=1' : ''}`);
        if (!res.ok) return null;
        const json = await res.json();
        const profile = json?.data?.profile || json?.profile || null;

        const cache = igLsRead();
        cache[h] = { profile, expiresAt: Date.now() + (profile ? IG_LS_TTL_OK_MS : IG_LS_TTL_NEG_MS) };
        // Drop expired entries to prevent unbounded growth
        const now = Date.now();
        for (const k of Object.keys(cache)) {
            if (!cache[k] || cache[k].expiresAt < now) delete cache[k];
        }
        igLsWrite(cache);

        return profile;
    } catch {
        return null;
    }
}

/**
 * Map auth role values to Knowledge Base role buckets used in UI/APIs.
 * - brand-ambassador => company
 * - solo-influencer => creator
 */
export function mapAuthRoleToKbRole(rawRole) {
    const role = String(rawRole || '').trim().toLowerCase();
    if (!role) return 'creator';
    if (role === 'brand-ambassador') return 'company';
    if (role === 'solo-influencer') return 'creator';
    if (role === 'company' || role === 'creator') return role;
    return 'creator';
}

/**
 * Resolve KB role from live auth user first, then fallback to `koenig-auth-user`.
 */
export function resolveKbRoleFromAuth(user) {
    const fromUser = String(user?.role || '').trim();
    if (fromUser) return mapAuthRoleToKbRole(fromUser);
    try {
        const raw = localStorage.getItem('koenig-auth-user');
        if (raw) {
            const parsed = JSON.parse(raw);
            return mapAuthRoleToKbRole(parsed?.role);
        }
    } catch {
        // ignore parse/storage errors
    }
    return 'creator';
}

// ─── Public client config (fetched once from backend, cached) ────────────
let _clientConfig = null;
let _clientConfigPromise = null;
export async function getClientConfig() {
    if (_clientConfig) return _clientConfig;
    if (_clientConfigPromise) return _clientConfigPromise;
    _clientConfigPromise = (async () => {
        try {
            const res = await fetch(`${getApiBaseUrl()}/api/health/config`);
            if (res.ok) {
                const json = await res.json();
                _clientConfig = json?.data || {};
            } else {
                _clientConfig = {};
            }
        } catch {
            _clientConfig = {};
        }
        return _clientConfig;
    })();
    return _clientConfigPromise;
}

/**
 * Fetch Open Graph metadata (title/description/image/favicon) for a URL.
 * Used by the website widget to auto-populate its card the moment a user pastes a link.
 * Returns null on any failure — callers should fall back to existing block data.
 */
export async function fetchLinkPreview(url) {
    if (!url || typeof url !== 'string') return null;
    try {
        const res = await fetch(`${getApiBaseUrl()}/api/link-preview?url=${encodeURIComponent(url)}`);
        if (!res.ok) return null;
        const json = await res.json();
        if (json?.data?.error) return null;
        return json?.data || null;
    } catch {
        return null;
    }
}

// ─── Knowledge Base API Configuration ────────────────────────────────────
// New hosted Knowledge Base URL (used for ingestion, content, chat, etc.)
export const KNOWLEDGE_BASE_URL = import.meta.env.VITE_KNOWLEDGE_BASE_URL || 'https://api-v1.kevo.store';
const KNOWLEDGE_BASE_API_KEY = import.meta.env.VITE_KNOWLEDGE_BASE_API_KEY || '11';

// ─── Voice (AI Voice) API Configuration ──────────────────────────────────
// Hostname is brand-driven via getDjangoApiBaseUrl() above; the key is
// shared across brands today.
export const VOICE_API_URL = getDjangoApiBaseUrl();
export const VOICE_API_KEY = getDjangoApiKey();

/**
 * Extract error message from API error response.
 * Handles both structured error responses (with detail array) and plain text errors.
 * @param {string} errorText - The error response text from the API
 * @returns {string} - The extracted error message
 */
function extractErrorMessage(errorText) {
    if (!errorText) return 'Request failed';

    try {
        const json = JSON.parse(errorText);
        // Check for structured error response with detail array
        if (json.detail && Array.isArray(json.detail) && json.detail.length > 0) {
            const firstError = json.detail[0];
            if (firstError.msg) {
                return firstError.msg;
            }
        }
        // Check for error field
        if (json.error?.message) {
            return json.error.message;
        }
        if (json.error) {
            return typeof json.error === 'string' ? json.error : JSON.stringify(json.error);
        }
        if (json.detail) {
            return typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
        }
    } catch (e) {
        // If parsing fails, return the text as-is
    }

    return errorText;
}

/**
 * OAuth callback URL for popup. When frontend and API are same origin, use /api/auth/callback so the request hits the backend (e.g. when proxy only forwards /api).
 */
export function getOAuthCallbackPath() {
    if (typeof window === 'undefined') return '/app/auth/callback';
    const apiBase = getApiBaseUrl().replace(/\/$/, '');
    const origin = window.location.origin;
    if (apiBase === origin || apiBase === origin + '/') {
        return `${apiBase}/api/auth/callback`;
    }
    return `${origin}/app/auth/callback`;
}

/**
 * Base headers for all API requests. Includes ngrok-skip-browser-warning so ngrok doesn't show the browser warning page.
 * @returns {Record<string, string>}
 */
export const getBaseHeaders = () => ({
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
});

const getNetworkErrorMsg = () =>
    'Unable to connect to server. Please ensure the backend is running at ' +
    getApiBaseUrl() +
    ' and try again.';

/**
 * POST /api/auth/login
 * @param {{ email: string, password: string }} body
 * @returns {Promise<{ ok: boolean, data?: { user, tokens }, error?: string }>}
 */
export async function loginApi(body) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/auth/login`, {
            method: 'POST',
            headers: getBaseHeaders(),
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data) {
            return { ok: true, data: json.data };
        }
        const message =
            json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message, details: json?.error?.details };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * Exchange a single-use handoff token (issued by /api/auth/handoff/login on another
 * origin) for the real user + access tokens. Used by the /auth/resolve route after a
 * cross-origin sign-in (e.g. from the Next.js SEO site).
 * POST /api/auth/handoff/exchange
 * @param {string} handoffToken
 * @returns {Promise<{ ok: boolean, data?: { user, tokens }, error?: string }>}
 */
export async function exchangeHandoffTokenApi(handoffToken) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/auth/handoff/exchange`, {
            method: 'POST',
            headers: getBaseHeaders(),
            body: JSON.stringify({ handoffToken }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data) {
            return { ok: true, data: json.data };
        }
        const message =
            json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * Backend base URL for OAuth (popup must open backend, not frontend).
 * If VITE_API_URL is same as current origin (e.g. both localhost:5173), use VITE_API_URL or localhost:4000.
 */
export function getOAuthBackendUrl() {
    const base = getApiBaseUrl();
    if (typeof window === 'undefined') return base;
    const origin = window.location.origin;
    if (base === origin || base === origin + '/') {
        return import.meta.env.VITE_API_URL || 'http://localhost:4000';
    }
    return base;
}

/**
 * Get OAuth auth URL from backend (for popup flow). Backend returns url + redirectUri; open url in popup; after callback, frontend calls verifyCodeApi with redirectUri.
 * @param {string} providerId - e.g. 'google'
 * @param {string} redirectUrl - frontend URL where backend will redirect with code (e.g. origin + '/auth/callback')
 * @returns {Promise<{ ok: boolean, url?: string, redirectUri?: string, error?: string }>}
 */
export async function getProviderAuthUrlFromApi(providerId, redirectUrl) {
    try {
        // Ensure we only send the frontend callback URL, never concatenated with redirectUri
        let cleanRedirectUrl = typeof redirectUrl === 'string' ? redirectUrl.trim() : '';
        const fragment = cleanRedirectUrl.indexOf('", "redirectUri"');
        if (fragment !== -1) cleanRedirectUrl = cleanRedirectUrl.slice(0, fragment).trim();
        const base = getApiBaseUrl();
        const res = await fetch(
            `${base}/api/auth/${encodeURIComponent(providerId)}/url?redirectUrl=${encodeURIComponent(cleanRedirectUrl)}`,
            { headers: getBaseHeaders() }
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data?.url) {
            return {
                ok: true,
                url: json.data.url,
                redirectUri: json.data.redirectUri || '',
            };
        }
        const message = json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * Verify OAuth code (popup flow). Call from callback page after backend redirected with code.
 * Backend decides redirect_uri from its own config; frontend only sends code + provider.
 * @param {{ code: string, provider: string }} body
 * @returns {Promise<{ ok: boolean, data?: { user, tokens }, error?: string }>}
 */
export async function verifyCodeApi(body) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/auth/verify-code`, {
            method: 'POST',
            headers: getBaseHeaders(),
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data) {
            return { ok: true, data: json.data };
        }
        const message = json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * Returns the backend URL to start OAuth (legacy full-page redirect).
 * @param {string} providerId - e.g. 'google'
 * @param {{ from?: string }} options - e.g. { from: 'settings' }
 * @returns {string}
 */
export function getProviderAuthUrl(providerId, options = {}) {
    const base = getOAuthBackendUrl();
    let state = typeof window !== 'undefined' ? window.location.origin : '';
    if (options.from) state = state ? `${state},from=${options.from}` : `from=${options.from}`;
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    const q = params.toString();
    return `${base}/api/auth/${encodeURIComponent(providerId)}${q ? `?${q}` : ''}`;
}

/** @deprecated Use getProviderAuthUrlFromApi + popup for new flow */
export function getGoogleAuthUrl() {
    return getProviderAuthUrl('google');
}

/**
 * POST /api/auth/register
 * @param {{ profileSlug: string, email: string, password: string }} body
 * @returns {Promise<{ ok: boolean, data?: { user, tokens }, error?: string }>}
 */
export async function registerApi(body) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/auth/register`, {
            method: 'POST',
            headers: getBaseHeaders(),
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success === true && json.data) {
            return { ok: true, data: json.data };
        }
        const message =
            json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message, details: json?.error?.details };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/auth/onboarding/import-collective
 * Trigger background import of all Collective brands and active products for the current user.
 * Call after register; returns 202 immediately while import runs in background.
 * @param {string} accessToken - Bearer token (e.g. from register response)
 * @returns {Promise<{ ok: boolean }>}
 */
export async function onboardingImportCollectiveApi(accessToken) {
    try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/auth/onboarding/import-collective`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (res.status === 202) return { ok: true };
        return { ok: false };
    } catch {
        return { ok: false };
    }
}




/**
 * POST /api/creator
 * Create a creator profile in the knowledge base system.
 */
export async function createCreatorApi(body) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/creator`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        const message = json?.error?.message || json?.detail || `Request failed (${res.status})`;
        return { ok: false, error: message };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/creator/{handle}
 * Fetch creator profile (includes hint_chips).
 */
export async function getCreatorApi(handle) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/creator/${encodeURIComponent(handle)}`, {
            headers: {
                ...getBaseHeaders(),
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        return { ok: false, error: json?.detail || `Request failed (${res.status})` };
    } catch (err) {
        return { ok: false, error: err?.message || 'Network error' };
    }
}



// kb status
export async function fetchKbStatus(accessToken) {

    try {
        const res = await fetch(`${getApiBaseUrl()}/api/account/kb-status`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });

        const json = await res.json().catch(() => ({}));

        if (res.ok) {
            return { ok: true, data: json.data || json };
        }
        return { ok: false, error: json?.message || 'Failed to fetch KB status' };

    } catch (err) {
        return { ok: false, error: err?.message || 'Network error' };
    }

}

/**
 * GET /api/inbox/counts/{creator}
 * Fetch inbox label counts for a creator (knowledge_gap, sales, self_optimized, poor_ai, golden_override).
 */
export async function fetchInboxCounts(creator) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/inbox/counts/${encodeURIComponent(creator)}`, {
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        return { ok: false, error: json?.detail || `Request failed (${res.status})` };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/inbox/sessions/{creator}?limit=&label=
 * Fetch inbox chat sessions for a creator, optionally filtered by label.
 * @param {{ creator: string, label?: string, limit?: number }} options
 */
export async function fetchInboxSessions({ creator, label, limit = 50 } = {}) {
    try {
        const params = new URLSearchParams();
        if (limit) params.set('limit', String(limit));
        if (label) params.set('label', label);
        const qs = params.toString();
        const url = `${KNOWLEDGE_BASE_URL}/api/inbox/sessions/${encodeURIComponent(creator)}${qs ? `?${qs}` : ''}`;
        const res = await fetch(url, {
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        return { ok: false, error: json?.detail || `Request failed (${res.status})` };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/inbox/correct/{creator}/{session_id}
 * Submit a Golden Override (corrected response) for a specific Q/A pair in a session.
 */
export async function postInboxCorrection({ creator, sessionId, originalQuery, originalResponse, correctedResponse } = {}) {
    try {
        const res = await fetch(
            `${KNOWLEDGE_BASE_URL}/api/inbox/correct/${encodeURIComponent(creator)}/${encodeURIComponent(sessionId)}`,
            {
                method: 'POST',
                headers: {
                    ...getBaseHeaders(),
                    accept: 'application/json',
                    'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                },
                body: JSON.stringify({
                    original_query: originalQuery,
                    original_response: originalResponse,
                    corrected_response: correctedResponse,
                }),
            },
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        return { ok: false, error: json?.detail || `Request failed (${res.status})` };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/inbox/chat/{creator}/{session_id}
 * Fetch the full chat (messages + classification + corrections) for a session.
 */
export async function fetchInboxChat({ creator, sessionId } = {}) {
    try {
        const res = await fetch(
            `${KNOWLEDGE_BASE_URL}/api/inbox/chat/${encodeURIComponent(creator)}/${encodeURIComponent(sessionId)}`,
            {
                headers: {
                    ...getBaseHeaders(),
                    accept: 'application/json',
                    'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                },
            },
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok) {
            return { ok: true, data: json };
        }
        return { ok: false, error: json?.detail || `Request failed (${res.status})` };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/content/{creator}
 * Fetch knowledge base content listing for a creator.
 * @param {string} creator - Creator username/slug (e.g. "uzair-anwar")
 * @returns {Promise<{ ok: boolean, data?: { creator: string, total_chunks: number, sources: array }, error?: string }>}
 */
export async function fetchKnowledgeContent(creator) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/content/${encodeURIComponent(creator)}`, {
            method: 'GET',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(text || `Request failed with status ${res.status}`);
        }
        const json = await res.json().catch(() => ({}));
        return { ok: true, data: json };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * DELETE /api/content
 * Delete a document from knowledge base by URL and creator.
 * @param {{ url: string, creator: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function deleteKnowledgeContent({ url, creator }) {
    try {
        if (!url) return { ok: false, error: 'Missing document URL' };
        if (!creator) return { ok: false, error: 'Missing creator' };

        const params = new URLSearchParams();
        params.set('url', url);
        params.set('creator', creator);

        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/content?${params.toString()}`, {
            method: 'DELETE',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                'ngrok-skip-browser-warning': 'true',
            },
        });

        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }

        const json = await res.json().catch(() => ({}));
        return { ok: true, data: json };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/substack
 * Connect and sync a Substack URL to knowledge base.
 * @param {{ url: string, creator: string }} body
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function connectSubstackSource({ url, creator }) {
    try {
        // Start Substack ingest job – returns a job_id which must be streamed
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/substack/start`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({ url, creator }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }
        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        // If error is already formatted (from extractErrorMessage), use it
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/shopmy
 * Connect and sync a Shopmy URL/username to knowledge base.
 * @param {{ username: string, creator: string }} body
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function connectShopmySource({ username, creator }) {
    try {
        // Start Shopmy ingest job – returns a job_id which must be streamed
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/shopmy/start`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({ username, creator }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }
        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        // If error is already formatted (from extractErrorMessage), use it
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/shopltk
 * Connect and sync a ShopLTK URL/username to knowledge base.
 * @param {{ username: string, creator: string }} body
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function connectShopltkSource({ username, creator }) {
    try {
        // Start ShopLTK ingest job – returns a job_id which must be streamed
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/shopltk/start`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({ username, creator }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }
        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        // If error is already formatted (from extractErrorMessage), use it
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/shopify/admin/start
 * Admin-side Shopify ingest — used by the regular merchant Knowledge Base
 * connect flow (the one inside KnowledgeSources.jsx). Same payload shape as
 * `startShopifyIngest` below; the only difference is the URL segment.
 * @param {{ domain: string, creator: string, include?: string[] }} body
 * @returns {Promise<{ ok: boolean, data?: object, error?: string, jobId?: string }>}
 */
export async function connectShopifySource({ domain, creator, ingest_policies = true, ingest_products = true, ingest_collections = true, ingest_pages = true, ingest_articles = true }) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/shopify/admin/start`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({ domain, creator, ingest_policies, ingest_products, ingest_collections, ingest_pages, ingest_articles }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }
        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/shopify/start
 * Non-admin variant of the Shopify ingest endpoint. Identical contract to
 * `connectShopifySource` (same body, headers, response shape) — only the
 * URL path differs (no `/admin` segment). Used exclusively by the admin
 * brand-crawl wizard via `seedKbForAdminProfile`; do not use elsewhere
 * without checking the KB-side route gating.
 * @param {{ domain: string, creator: string, include?: string[] }} body
 * @returns {Promise<{ ok: boolean, data?: object, error?: string, jobId?: string }>}
 */
export async function startShopifyIngest({ domain, creator, ingest_policies = true, ingest_products = true, ingest_collections = true, ingest_pages = true, ingest_articles = true }) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/shopify/store/start`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({ domain, creator, ingest_policies, ingest_products, ingest_collections, ingest_pages, ingest_articles }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }
        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/doc/start
 * Upload and sync a document to knowledge base (multipart/form-data).
 * Backend expects:
 *  - Query string: ?creator=<creator>
 *  - FormData: document=<binary>
 * @param {{ file: File, creator: string }} body
 * @returns {Promise<{ ok: boolean, data?: object, jobId?: string, error?: string }>}
 */
export async function connectDocSource({ file, creator }) {
    try {
        if (!file) return { ok: false, error: 'Please select a document first.' };
        if (!creator) return { ok: false, error: 'Missing creator.' };

        const form = new FormData();
        form.append('document', file);

        const res = await fetch(
            `${KNOWLEDGE_BASE_URL}/api/ingest/doc/start?creator=${encodeURIComponent(creator)}`,
            {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                    'ngrok-skip-browser-warning': 'true',
                },
                body: form,
            }
        );

        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }

        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/ingest/faq/start
 * Start FAQ ingest using either pasted text OR uploaded file.
 * Accepts:
 *  - JSON: { creator, text, source_label }
 *  - multipart/form-data: creator, source_label, faq_file
 * @param {{ creator: string, sourceLabel?: string, text?: string, file?: File }} body
 * @returns {Promise<{ ok: boolean, data?: object, jobId?: string, error?: string }>}
 */
export async function connectFaqSource({ creator, sourceLabel, text, file }) {
    try {
        if (!creator) return { ok: false, error: 'Missing creator.' };

        const hasText = typeof text === 'string' && text.trim().length > 0;
        const hasFile = !!file;

        if (!hasText && !hasFile) {
            return { ok: false, error: 'Please paste FAQ text or upload a FAQ file.' };
        }

        let res;
        if (hasFile) {
            const form = new FormData();
            form.append('creator', creator);
            if (sourceLabel) form.append('source_label', sourceLabel);
            form.append('faq_file', file);

            res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/faq/start`, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                    'ngrok-skip-browser-warning': 'true',
                },
                body: form,
            });
        } else {
            res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/faq/start`, {
                method: 'POST',
                headers: {
                    ...getBaseHeaders(),
                    accept: 'application/json',
                    'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                },
                body: JSON.stringify({
                    creator,
                    text: text.trim(),
                    source_label: sourceLabel || 'FAQ',
                }),
            });
        }

        if (!res.ok) {
            const textRes = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(textRes) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }

        const json = await res.json().catch(() => ({}));
        const jobId = json.job_id || json.jobId;
        if (!jobId) {
            return { ok: false, error: 'Missing job_id in response from server.' };
        }
        return { ok: true, data: json, jobId };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        if (err?.message && err.message !== 'Failed to fetch') {
            return { ok: false, error: err.message };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * SSE stream for ingest jobs.
 * GET /api/ingest/stream/{job_id}
 * Streams log / ping / done events for a running ingest job.
 * @param {string} jobId
 * @param {{ signal?: AbortSignal, onLog?: (msg: string) => void, onPing?: () => void, onDone?: () => void }} callbacks
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function streamIngestJob(jobId, { signal, onLog, onPing, onDone } = {}) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/ingest/stream/${encodeURIComponent(jobId)}`, {
            method: 'GET',
            headers: {
                accept: 'text/event-stream',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                'ngrok-skip-browser-warning': 'true',
            },
            signal,
        });

        if (!res.ok || !res.body) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                const jsonStr = line.slice(6).trim();
                if (!jsonStr) continue;

                try {
                    const data = JSON.parse(jsonStr);
                    if (data.type === 'log' && typeof data.message === 'string') {
                        onLog && onLog(data.message);
                    } else if (data.type === 'ping') {
                        onPing && onPing();
                    } else if (data.type === 'done') {
                        onDone && onDone();
                    }
                } catch {
                    // ignore malformed lines
                }
            }
        }

        return { ok: true };
    } catch (err) {
        if (err.name === 'AbortError') {
            return { ok: false, error: 'Request cancelled' };
        }
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/shopify/store/info
 * Fetch connected Shopify store details for current user.
 * @param {{ accessToken: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function fetchShopifyStoreInfo({ accessToken }) {
    try {
        if (!accessToken) return { ok: false, error: 'Missing access token' };
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/shopify/store/info`, {
            method: 'GET',
            headers: {
                ...getBaseHeaders(),
                accept: 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.success === true) {
            return { ok: true, data: json?.data || {} };
        }
        const message = json?.error?.message || (res.ok ? 'Invalid response' : `Request failed (${res.status})`);
        return { ok: false, error: message };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/onboarding/connect-shopify
 * Connect Shopify store in app backend (primary DB).
 * @param {{ accessToken: string, shopDomain: string }} params
 * @returns {Promise<{ ok: boolean, data?: object, error?: string }>}
 */
export async function connectShopifyStoreApp({ accessToken, shopDomain }) {
    try {
        if (!accessToken) return { ok: false, error: 'Missing access token' };
        if (!shopDomain) return { ok: false, error: 'Missing shop domain' };
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/onboarding/connect-shopify`, {
            method: 'POST',
            headers: {
                ...getBaseHeaders(),
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ shopDomain }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.success === true) {
            return { ok: true, data: json?.data || {} };
        }
        return {
            ok: false,
            error: json?.error?.message || `Request failed (${res.status})`,
        };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * GET /api/shopify/store/connection
 * Get app-side store connection status.
 * @param {{ accessToken: string }} params
 * @returns {Promise<{ ok: boolean, data?: { connected?: boolean, shopDomain?: string|null }, error?: string }>}
 */
export async function fetchShopifyStoreConnection({ accessToken }) {
    try {
        if (!accessToken) return { ok: false, error: 'Missing access token' };
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/api/shopify/store/connection`, {
            method: 'GET',
            headers: {
                ...getBaseHeaders(),
                Authorization: `Bearer ${accessToken}`,
            },
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.success === true && json?.data) {
            return { ok: true, data: json.data };
        }
        return {
            ok: false,
            error: json?.error?.message || `Request failed (${res.status})`,
        };
    } catch (err) {
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: getNetworkErrorMsg() };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

/**
 * POST /api/chat/stream
 * Send a chat message to the knowledge base API with streaming support.
 * @param {{ message: string, creator: string, session_id: string, source_filter?: string, signal?: AbortSignal }} params
 * @param {{ onToken?: (content: string) => void, onDone?: (content: string, products?: any[]) => void, onProducts?: (products: any[]) => void }} callbacks
 * @returns {Promise<{ ok: boolean, data?: { content: string, products?: any[] }, error?: string }>}
 */
export async function sendChatStream({ message, creator, session_id, source_filter, signal }, callbacks = {}) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/chat/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
                'ngrok-skip-browser-warning': 'true',
            },
            body: JSON.stringify({
                message: message.trim(),
                creator,
                session_id,
                ...(source_filter ? { source_filter } : {})
            }),
            signal
        });

        if (!res.ok) {
            const text = await res.text().catch(() => '');
            const errorMsg = extractErrorMessage(text) || `Request failed with status ${res.status}`;
            return { ok: false, error: errorMsg };
        }

        // Check content type to determine if it's streaming or regular JSON
        const contentType = res.headers.get('content-type') || '';
        const isStreaming = contentType.includes('text/event-stream') || contentType.includes('text/plain');

        if (isStreaming && res.body) {
            // Handle streaming response (SSE format)
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let assistantMessage = '';
            let products = null;
            let chunkHashes = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const jsonStr = line.slice(6).trim();
                    if (!jsonStr) continue;

                    try {
                        const data = JSON.parse(jsonStr);

                        if (data.type === 'token') {
                            assistantMessage += data.content;
                            if (callbacks.onToken) {
                                callbacks.onToken(data.content);
                            }
                        } else if (data.type === 'done') {
                            assistantMessage = data.content || assistantMessage;
                            products = data.products || null;
                            chunkHashes = data.chunk_hashes || null;
                            if (callbacks.onDone) {
                                callbacks.onDone(assistantMessage, products, chunkHashes);
                            }
                        } else if (data.type === 'products' && data.products) {
                            products = data.products;
                            if (callbacks.onProducts) {
                                callbacks.onProducts(data.products);
                            }
                        }
                    } catch (e) {
                        // Ignore malformed SSE lines
                        // console.warn('Failed to parse SSE chunk', e, line);
                    }
                }
            }

            return {
                ok: true,
                data: {
                    content: assistantMessage,
                    products: products || null,
                    chunk_hashes: chunkHashes || null
                }
            };
        } else {
            // Handle regular JSON response
            const data = await res.json();
            const responseText = data.response || data.message || data.content || 'Sorry, I could not process that.';

            return {
                ok: true,
                data: {
                    content: responseText,
                    products: data.products || null
                }
            };
        }
    } catch (err) {
        if (err.name === 'AbortError') {
            return { ok: false, error: 'Request cancelled' };
        }
        if (err instanceof TypeError && err.message === 'Failed to fetch') {
            return { ok: false, error: 'Unable to connect to server. Please try again.' };
        }
        return { ok: false, error: err?.message || 'Network error' };
    }
}

// ─── Chat persistence helpers ─────────────────────────────────────────────────

/**
 * Persistent visitor id for unique-visitor tracking across sessions.
 */
export function getOrCreateVisitorId() {
    if (typeof window === 'undefined') return null;
    const key = 'visitor_id';
    try {
        let id = localStorage.getItem(key);
        if (!id) {
            id = 'v_' + Math.random().toString(36).slice(2, 14) + '_' + Date.now().toString(36);
            localStorage.setItem(key, id);
        }
        return id;
    } catch (e) {
        return null;
    }
}

/**
 * Save a chat message to the backend.
 */
export async function saveChatMessage({ sessionId, slug, role, content, products, visitorId }) {
    try {
        if (!slug) { console.warn('[saveChatMessage] no slug, skipping'); return null; }
        const res = await fetch(`${getApiBaseUrl()}/api/chat/message`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, slug, role, content, products, visitorId }),
        });
        if (!res.ok) { console.warn('[saveChatMessage] failed', res.status, await res.text().catch(() => '')); return null; }
        const json = await res.json();
        return json.data ?? null;
    } catch (err) {
        console.warn('[saveChatMessage] error', err);
        return null;
    }
}

/**
 * Save a lead form submission to the backend.
 */
export async function saveChatLead({ sessionId, slug, name, email, phone, formData }) {
    try {
        if (!slug) { console.warn('[saveChatLead] no slug, skipping'); return null; }
        const res = await fetch(`${getApiBaseUrl()}/api/chat/lead`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, slug, name, email, phone, formData }),
        });
        if (!res.ok) { console.warn('[saveChatLead] failed', res.status, await res.text().catch(() => '')); return null; }
        const json = await res.json();
        return json.data ?? null;
    } catch (err) {
        console.warn('[saveChatLead] error', err);
        return null;
    }
}

// ─── 3rd-party KB tracking APIs (cart-click & purchase) ─────────────────────

/**
 * Track "Add to Cart" click on a product card shown in chat AI response.
 * Fires against the Knowledge Base URL.
 */
export async function trackCartClick({ creator, sessionId, product, queryText, aiResponseHash, chunkHashes, conversation }) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/track/cart-click`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({
                creator,
                session_id: sessionId,
                product: {
                    id: product.id,
                    title: product.title,
                    url: product.url,
                    price: product.price,
                    brand: product.brand,
                    platform: product.platform || '',
                    image_url: product.image_url || '',
                },
                query_text: queryText || '',
                ai_response_hash: aiResponseHash || '',
                chunk_hashes: chunkHashes || [],
                conversation: conversation || [],
            }),
        });
        if (!res.ok) console.warn('[trackCartClick] failed', res.status);
        return { ok: res.ok };
    } catch (err) {
        console.warn('[trackCartClick] error', err);
        return { ok: false, error: err?.message };
    }
}

/**
 * Track purchase completion (Proceed to Checkout) for a product from chat.
 * Fires against the Knowledge Base URL.
 */
export async function trackPurchaseComplete({ creator, sessionId, product, queryText, aiResponseHash, chunkHashes, conversation, orderReference }) {
    try {
        const res = await fetch(`${KNOWLEDGE_BASE_URL}/api/track/purchase`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': KNOWLEDGE_BASE_API_KEY,
            },
            body: JSON.stringify({
                creator,
                session_id: sessionId,
                product: {
                    id: product.id,
                    title: product.title,
                    url: product.url,
                    price: product.price,
                    brand: product.brand,
                    platform: product.platform || '',
                    image_url: product.image_url || '',
                },
                query_text: queryText || '',
                ai_response_hash: aiResponseHash || '',
                chunk_hashes: chunkHashes || [],
                conversation: conversation || [],
                order_reference: orderReference || '',
            }),
        });
        if (!res.ok) console.warn('[trackPurchaseComplete] failed', res.status);
        return { ok: res.ok };
    } catch (err) {
        console.warn('[trackPurchaseComplete] error', err);
        return { ok: false, error: err?.message };
    }
}
